import { normalizeSelectedActor } from 'app/shared/utils/actor-identifiers.util';
import { isSuperAdminClaims } from 'app/shared/utils/super-admin-access.util';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { UserProfile, User_UserProfile } from 'app/modules/models/profile-management/userProfile.type';
import { RoleService } from 'app/shared/services/role.service';
import { environment } from 'environments/environment';
import {
  BehaviorSubject, catchError, filter,
  forkJoin, lastValueFrom, map, Observable, of, ReplaySubject, switchMap, take, tap, throwError
} from 'rxjs';
import { User } from '../user/user.types';
import { PartnersService } from 'app/shared/services/partners.service';
import { StoreService } from '../../ngrx/store.service';
import { UserPreferencesService } from 'app/shared/services/userPreferences.service';
import { isSuperAdmin, UserRoleUtils } from 'app/shared/utils';

@Injectable()
export class AuthService {
  private authUri: string = environment.IDENTITY_URL;
  private _authenticated: boolean = false;

  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  userRoleUtils: UserRoleUtils;

  /**
   * Constructor
   */
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _userService: UserService,
    private readonly _roleService: RoleService,
    private readonly _partnersService: PartnersService,
    private readonly storeService:StoreService,
    private readonly _userPreferencesService: UserPreferencesService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }
  /**
   * Setter & getter for access token
   */
  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    let queryParam = '?email=' + email;
    return this._httpClient.post(this.authUri + 'api/auth/forgot-password' + queryParam, {});
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/reset-password', password);
  }

  /**
   * Dissociate Google using credentials (email + password)
   */
  dissociateGoogleWithCredentials(payload: { email: string; password: string }): Observable<any> {
    // Some backend endpoints return 200 with an empty body which causes
    // Angular HttpClient's default JSON parser to throw. Request the
    // response as text to avoid JSON parse errors and keep compatibility.
    return this._httpClient.post(this.authUri + 'api/auth/external/unlink-google', payload, { responseType: 'text' as 'json' });
  }

  /**
   * Dissociate Microsoft using credentials (email + password)
   */
  dissociateMicrosoftWithCredentials(payload: { email: string; password: string }): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/external/unlink-microsoft', payload, { responseType: 'text' as 'json' });
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { username: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated && this.accessToken) {
      return throwError('User is already logged in.');
    }

    return this._httpClient.post(this.authUri + 'api/auth/sign-in', credentials).pipe(
      switchMap((response: any) => {
        let jwtData = response.token.split('.')[1];
        let decodedJwtJsonData = window.atob(jwtData);
        let decodedJwtData = JSON.parse(decodedJwtJsonData);

        // Stocker les données de l'utilisateur
        let connectedUser = {
          id: decodedJwtData.id,
          email: decodedJwtData.Email ?? decodedJwtData.email ?? decodedJwtData['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
          fullname: decodedJwtData.name,
          role: decodedJwtData.role,
          status: decodedJwtData.status,
          tenantId: decodedJwtData.TenantId,
          tenant: decodedJwtData.Tenant,
          firstname: decodedJwtData.FirstName,
          lastname: decodedJwtData.LastName,
          enabled: decodedJwtData.IsEnabled,
          isOnboarded: decodedJwtData.IsOnboarded,
          onSICA: decodedJwtData.OnSICA,
          onACEP: decodedJwtData.OnACEP,
        };

        this.storeService.setConnectedUser(connectedUser);

        // Store the access token & refresh token in the local storage
        this.accessToken = response.token;
        this.refreshToken = response.refreshToken;

        const isSuperAdminToken = isSuperAdminClaims(decodedJwtData);
        // The authenticated SuperAdmin account has no tenant profile/IsEnabled claim.
        if (!isSuperAdminToken && !this.isEnabledClaim(decodedJwtData.IsEnabled)) {
          this.signOut();
          return of(null); // Retourne un Observable pour éviter l'erreur
        }

        this._authenticated = true;

        return this._userService.signIn(credentials.username).pipe(
          switchMap((user: any) => {
            this._userService.connectedUser = user;
            let currentUser = this.storeService.getConnectedUser();
            currentUser = {...user, ...currentUser,
              id : user?.id || currentUser?.id,
              cguAccepted : user?.cguAccepted ?? currentUser?.cguAccepted ?? true
              ,cguAcceptedOn : user?.cguAcceptedOn
              ,onboardedOnVersion : user?.onboardedOnVersion,
              userProfiles : user?.userProfiles ? user.userProfiles : [],
              user_UserProfiles : user?.user_UserProfiles ? user?.user_UserProfiles : []
            };
            this.storeService.setConnectedUser(currentUser);
            this._userService.connectedUser = currentUser;
            this.setTeraActorContext(currentUser);
            const isTeraInternalUser = !isSuperAdmin() && !currentUser?.teraActorId;
            this.storeService.setIsPartnerAdmin(
              isTeraInternalUser || this.isPartnerAdmin(user?.user_UserProfiles)
            );
            this.storeService.setIsMandataire(this.isMandataire(user?.user_UserProfiles));
            this.setIsAcepPartner(user?.user_UserProfiles);
            const checkUserProfile$ = this.checkUserProfile(user) ?? of(null);
            const checkDefautRoles$ = isSuperAdmin() && user?.companyId
              ? this.checkDefautRoles(user.companyId)
              : of(null);

            return forkJoin({
              checkUserProfile: checkUserProfile$,
              checkDefautRoles: checkDefautRoles$,
            }).pipe(map(() => currentUser)); // Retourner `currentUser` après exécution
          })
        );
      }),
      catchError(error => {
        this.signOut();
        return throwError(() => error);
      })
    );
  }

  private isEnabledClaim(value: unknown): boolean {
    return value === true || String(value).toLowerCase() === 'true';
  }

  setIsAcepPartner(userProfile){
    this.userRoleUtils = new UserRoleUtils();
    this.storeService.setIsAcepPartner(this.userRoleUtils.isAcepPartenaireRole(userProfile));
  }

  isPartnerAdmin(userUserProfiles: User_UserProfile[]): boolean {
    if (!userUserProfiles) {
      return false;
    }
    return userUserProfiles.some(
      profile =>
        profile?.userProfile?.name === 'Admin E. Partenaire'
    );
  }

  isMandataire(userUserProfiles: User_UserProfile[]): boolean {
    if (!userUserProfiles) {
      return false;
    }
    return userUserProfiles.some(
      profile => {
        const roleName = profile?.userProfile?.name?.toLowerCase() ?? '';
        return roleName.includes('mandataire') || roleName === 'superviseur';
      }
    );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    if (!this.accessToken) {
      return of(false);
    }
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        switchMap(() => of(true))
      );
    } else {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      if (AuthUtils.isTokenExpired(this.accessToken)) {
      return this._httpClient.post(this.authUri + 'api/auth/refresh-token', {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
      }).pipe(
        switchMap((response: any) => {
          const refreshedAccessToken = response?.accessToken || response?.token;
          if (!refreshedAccessToken) {
            return this.rejectTeraSession();
          }
          this.accessToken = refreshedAccessToken;
          if (response.refreshToken) {
            this.refreshToken = response.refreshToken;
          }
          let jwtData = refreshedAccessToken.split('.')[1];
          let decodedJwtJsonData = window.atob(jwtData);
          let decodedJwtData = JSON.parse(decodedJwtJsonData);
          return this._userService.fetchUserByEmail(decodedJwtData.Email).pipe(
            tap((user: any) => {
              this.restoreUserSession(user);
            }),
            switchMap(() => {
              this._authenticated = true;
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(this.accessToken);
              return of(true);
            }),
            catchError(() => this.rejectTeraSession())
          );
        }),
        catchError(() => this.rejectTeraSession())
      );
    } else {
      let jwtData = this.accessToken.split('.')[1];
      let decodedJwtJsonData = window.atob(jwtData);
      let decodedJwtData = JSON.parse(decodedJwtJsonData);
      return this._userService.fetchUserByEmail(decodedJwtData.Email).pipe(
        tap((user: any) => {
          this.restoreUserSession(user);
        }),
        switchMap(() => {
          this._authenticated = true;
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(this.accessToken);
          return of(true);
        }),
        catchError(() => this.rejectTeraSession())
      );
    }
  }
  }

  private restoreUserSession(user: User): void {
    this._userService.connectedUser = user;
    this.storeService.setConnectedUser(user);
    this.setTeraActorContext(user);
    const isTeraInternalUser = !isSuperAdmin() && !user?.teraActorId;
    this.storeService.setIsPartnerAdmin(
      isTeraInternalUser || this.isPartnerAdmin(user?.user_UserProfiles)
    );
    this.storeService.setIsMandataire(this.isMandataire(user?.user_UserProfiles));
    this.setIsAcepPartner(user?.user_UserProfiles);
    this._userPreferencesService.loadMine().subscribe({ error: () => { } });
  }

  private rejectTeraSession(): Observable<boolean> {
    this.refreshTokenInProgress = false;
    this.refreshTokenSubject.next(null);
    this.signOut();
    return of(false);
  }

  checkUserProfile(user: User) :Observable<any>{
    if(user?.user_UserProfiles && this.checkUserRoles(user?.user_UserProfiles)) {
      // check if local storage contains the selected partener 
      let selectedPartener = this.storeService.getUserPartner();
      if (!selectedPartener && !this.storeService.IsPartnerAdmin()) {
        if (user?.teraActor?.actorType?.code === 'AC2') {
          this.setTeraActorContext(user);
        } else if(user?.companyId) {
         this._partnersService.getPartnerByCompanyId(user.companyId).subscribe(partner => {
          if (partner) {
            this.storeService.setUserPartner(partner);
          }
         });
        }
      }
    }
    return of(true);
  }

  checkUserRoles(userUserProfiles: User_UserProfile[]): Observable<boolean> {
    if (!userUserProfiles) {
      return of(false);
    }
    const res= userUserProfiles?.some(
      profile =>
        profile?.userProfile?.name === 'Admin E. Partenaire' ||
        profile?.userProfile?.name === 'Partenaire ACEP' ||
        profile?.userProfile?.name === 'Partenaire ACEP Projets'
    );
    return  of(res)
  }

  private setTeraActorContext(user: User): void {
    const actor = user?.teraActor;
    if (!actor || actor.actorType?.code !== 'AC2' || this.storeService.getUserPartner()) {
      return;
    }

    const partner = normalizeSelectedActor({
      ...actor,
      id: actor.id,
      guidId: actor.id,
      numericId: actor.numericId,
      code: actor.code,
      codeClient: actor.code,
      name: actor.name,
      nom: actor.name,
      siret: actor.siret,
      email: actor.email,
      tel: actor.phone,
      isActive: actor.isActive,
      isDeleted: actor.isDeleted
    });
    this.storeService.setUserPartner(partner);
    this._partnersService.setCurrentPartner(partner as any);
  }

  checkDefautRoles(companyId: string): Observable<any> {
    return this._roleService.getRolesByCompanyId(companyId, true).pipe(
      switchMap((roles: UserProfile[]) => {
        if (roles.length === 0) {
          let defaultRoles: UserProfile[] = [
            {
              name: 'Propriétaire',
              description: 'Propriétaire: à accès à toutes les pages ainsi que au paramétrage ',
              companyId: companyId,
              isCommon: true,
              permissions: [],
              code: 'owner',
            },
            {
              name: 'Admin',
              description: 'Admin: à accès à toutes les pages ainsi que au paramétrage ',
              companyId: companyId,
              isCommon: true,
              permissions: [],
              code: 'admin',
            },
            {
              name: 'Basique',
              description: 'Basique: à accès à toutes les pages sauf au paramétrage',
              companyId: companyId,
              isCommon: true,
              permissions: [],
              code: 'basic',
            },
          ];
          return this._roleService.addRange(defaultRoles); // Retourner l'Observable
        }
        return of(null); // Si les rôles existent déjà, on retourne un Observable vide
      })
    );
  }


  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    const acceptCookies = localStorage.getItem('accept-cookies');
    const rememberMe = localStorage.getItem('rememberMe');
    const email = localStorage.getItem('email');
    localStorage.clear();
    if (acceptCookies !== null) {
      localStorage.setItem('accept-cookies', acceptCookies);
    }

    if(rememberMe){
      localStorage.setItem('email', email ? email : '');
    }
    this.storeService.setConnectedUser(null);
    this.storeService.setCurrentCompany(null);
    this.storeService.setIsPartnerAdmin(false);
    this.storeService.setIsAcepPartner(false);
    this.storeService.setUserPartner(null);
    this.storeService.setIsMandataire(false);

    // Clear the in-memory user as well. Components such as the global
    // Admin Espace Partenaire banner subscribe to this source directly.
    this._userService.connectedUser = null;

    // Set the authenticated flag to false
    this._authenticated = false;

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    company: string;
  }): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/sign-up', user);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post(this.authUri + 'api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }

    // If the access token exists and it didn't expire, sign in using it
    return this.signInUsingToken();
  }

  isTokenExpired(): boolean {
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return true;
    } else {
      return false;
    }
  }

  hasToken(): boolean {
    if (!this.accessToken) {
      return false;
    } else {
      return true;
    }
  }

  generateToken(email: string): Observable<any> {
    return this._httpClient
      .post(this.authUri + 'api/auth/generate-token/' + `${email}` + '/' + false
        , email, this.prepareHeader())
      .pipe(map(res => res as any));
  }

  generateRefreshToken(): Observable<any> {
    return this._httpClient
     .post(this.authUri + 'api/auth/refresh-token',  { refreshToken:this.refreshToken , accessToken: this.accessToken }, this.prepareHeader())
     .pipe(map(res => res as any));
  }


  private prepareHeader(): Object {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/json');
    return { headers: headers };
  }
}
